import { useId, useRef, useState } from 'react'
import { cn } from '#app/utils/misc'
import { useFrameLoop, useReducedMotionSafe } from '#app/utils/site/scene-clock'
import {
	IDLE_RENTER_CYCLE,
	RENTER_PROFILES,
	RENTER_SCENE_COPY,
	type RenterCycle,
	type RenterPhase,
	advanceRenterCycle,
	jumpToRenterProfile,
	renterPhaseAt,
	renterSceneView,
	renterTabForKey,
	startRenterCycle,
} from '#app/utils/site/scenes/renter-profile'
import { CheckIcon } from '../../primitives'
import type { SceneProps } from './types'

/**
 * "Built For Every Renter" ("renter-profile"): four renter profiles take turns
 * on one landlord-view card. Each turn strikes through the credit word, raises
 * the ID / Income / Rent History rows and stamps APPROVED. The chips are a
 * tablist; a click or an arrow key jumps the card there and restarts the
 * timer. Hovering the tile or focusing a chip holds the current profile.
 * The schedule is pure, in app/utils/site/scenes/renter-profile.ts; the
 * choreography keyed on `data-phase` is in app/styles/site/scene-renter-profile.css.
 */
export function RenterProfileScene({ active }: SceneProps) {
	const id = useId()
	const { cycle, phase, view, select, setPaused } = useRenterCycle(active)
	const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
	const profile = RENTER_PROFILES[view.shownIndex] ?? RENTER_PROFILES[0]
	const panelId = `${id}-panel`
	const tabId = (index: number) => `${id}-tab-${index}`

	return (
		<div
			data-scene="renter-profile"
			data-active={active || undefined}
			data-phase={phase}
			className="tt-scene-renter-profile flex shrink-0 flex-col items-center gap-[18px]"
			onPointerEnter={(event) => {
				if (event.pointerType !== 'touch') setPaused('hover', true)
			}}
			onPointerLeave={() => setPaused('hover', false)}
		>
			<div
				role="tablist"
				aria-label={RENTER_SCENE_COPY.tablist}
				className="flex flex-wrap justify-center gap-1.5"
				onFocus={() => setPaused('focus', true)}
				onBlur={(event) => {
					const next = event.relatedTarget as Node | null
					if (!event.currentTarget.contains(next)) setPaused('focus', false)
				}}
			>
				{RENTER_PROFILES.map((item, index) => {
					const selected = index === cycle.index
					return (
						<button
							key={item.tag}
							ref={(element) => {
								tabRefs.current[index] = element
							}}
							type="button"
							role="tab"
							id={tabId(index)}
							aria-selected={selected}
							aria-controls={panelId}
							tabIndex={selected ? 0 : -1}
							onClick={() => select(index)}
							onKeyDown={(event) => {
								const target = renterTabForKey(event.key, index)
								if (target === null) return
								event.preventDefault()
								tabRefs.current[target]?.focus()
								select(target)
							}}
							className={cn(
								'whitespace-nowrap rounded-full border px-3 py-[7px] text-[12.5px] font-semibold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink focus-visible:ring-offset-2 max-[900px]:px-[11px] max-[900px]:text-[12px]',
								selected
									? 'border-tt-ink bg-tt-ink text-white'
									: 'border-tt-ink/14 bg-white/60 text-tt-body hover:border-tt-ink/30 hover:bg-white',
							)}
						>
							{item.tag}
						</button>
					)
				})}
			</div>

			<div
				role="tabpanel"
				id={panelId}
				aria-labelledby={tabId(cycle.index)}
				className="tt-scene-renter-profile__card relative w-full max-w-[460px] overflow-hidden rounded-[24px] bg-white p-[26px] shadow-tt-card max-[900px]:p-[18px]"
			>
				<div
					className={cn(
						'tt-scene-renter-profile__header flex items-center gap-3 pr-[120px] transition-opacity duration-300 max-[900px]:pr-24',
						view.textsHidden && 'opacity-0',
					)}
				>
					<span
						aria-hidden="true"
						className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-[linear-gradient(135deg,var(--color-tt-pink),var(--color-tt-lav))] text-[14px] font-bold text-tt-ink"
					>
						{profile.initials}
					</span>
					<div className="min-w-0">
						<b className="block text-[19px] font-semibold tracking-[-0.01em]">
							{profile.name}
						</b>
						<span className="mt-[5px] inline-block rounded-full bg-[#e3edd3] px-2.5 py-1 text-[12px] font-semibold text-[#3f5a22]">
							{profile.tag}
						</span>
					</div>
				</div>

				<div className="mt-[22px] text-[11px] font-bold uppercase tracking-[0.14em] text-tt-muted">
					{RENTER_SCENE_COPY.credit}
				</div>
				<div
					className={cn(
						'tt-scene-renter-profile__score tt-scene-renter-profile__condensed relative mt-1.5 inline-block whitespace-nowrap leading-[0.9] text-[#8f918c] transition-opacity duration-300',
						view.textsHidden && 'opacity-0',
					)}
				>
					{profile.score}
				</div>

				<div className="mt-[22px] grid grid-cols-3 gap-2">
					{RENTER_SCENE_COPY.rows.map((row) => (
						<div
							key={row}
							className="tt-scene-renter-profile__row flex flex-col items-center justify-center gap-2.5 rounded-[14px] bg-[#f7faf2] px-2 py-4 text-center text-[14px] font-semibold max-[900px]:text-[13px]"
						>
							<CheckIcon />
							{row}
						</div>
					))}
				</div>

				<div className="tt-scene-renter-profile__stamp tt-scene-renter-profile__condensed absolute top-6 right-[22px] rounded-[12px] border-[2.5px] border-tt-ok bg-white px-3 py-[7px] text-[22px] leading-none text-tt-ok max-[900px]:top-[18px] max-[900px]:right-3.5 max-[900px]:px-[9px] max-[900px]:py-[5px] max-[900px]:text-[16px]">
					{RENTER_SCENE_COPY.stamp}
				</div>
			</div>
		</div>
	)
}

type PauseReason = 'hover' | 'focus'

/**
 * Drives the schedule from the animation frame, no effect. The clock starts
 * the first frame after `active` turns true and rewinds when it turns false,
 * so the scene replays each time it scrolls into view. Reduced motion shows
 * the finished card and lets the chips swap it instantly.
 */
function useRenterCycle(active: boolean) {
	const reduced = useReducedMotionSafe()
	const [cycle, setCycle] = useState<RenterCycle>(IDLE_RENTER_CYCLE)
	const [phase, setPhase] = useState<RenterPhase>('idle')
	const [wasActive, setWasActive] = useState(active)
	const pausedRef = useRef<Record<PauseReason, boolean>>({
		hover: false,
		focus: false,
	})
	const clockRef = useRef(0)

	// Adjusting state during render (the React-documented pattern) instead of
	// an effect: when the tile scrolls out, rewind so it replays on return.
	if (active !== wasActive) {
		setWasActive(active)
		if (!active) {
			setCycle(IDLE_RENTER_CYCLE)
			setPhase('idle')
		}
	}

	// Takes frames only while the tile is in view with motion allowed; the
	// clock restarts at 0 each run, matching the rewind above.
	useFrameLoop(active && !reduced, (time) => {
		clockRef.current = time
		const paused = pausedRef.current.hover || pausedRef.current.focus
		const next =
			cycle.startedAt === null
				? startRenterCycle(time, cycle.index)
				: advanceRenterCycle(cycle, time, paused)
		if (next !== cycle) setCycle(next)
		const nextPhase = renterPhaseAt(time - (next.startedAt ?? time))
		if (nextPhase !== phase) setPhase(nextPhase)
	})

	const shownPhase: RenterPhase = reduced ? 'on' : phase
	const view = renterSceneView(cycle, shownPhase)

	return {
		cycle,
		phase: shownPhase,
		view,
		select(target: number) {
			setCycle(
				jumpToRenterProfile(cycle, target, clockRef.current, view.shownIndex),
			)
			// Flip to the swap now rather than a frame later, so the new profile
			// never flashes at full strength before its fade.
			if (cycle.startedAt !== null && !reduced) setPhase('swap')
		},
		setPaused(reason: PauseReason, value: boolean) {
			pausedRef.current[reason] = value
		},
	}
}
